import React, { CSSProperties, ReactNode, useState } from 'react';
import { useSuccessAlert } from '../../../App.hooks';
import DialogBase from '../../../base/DialogBase/DialogBase';
import { ContentVersion, useGetUsersWithoutRoleQuery, useReassignRequestMutation } from '../../../generated/graphql';

type AssignResourceToRequestDialogProps = {
    onClose: () => void;
    visible: boolean;
    style?: CSSProperties;
    children?: ReactNode;
    request: ContentVersion | undefined;
};

const AssignResourceToRequestDialog = ({ visible, onClose, request, style }: AssignResourceToRequestDialogProps) => {
    const [assignedToId, setAssignedToId] = useState('');
    const [comment, setComment] = useState('');
    const [error, setError] = useState<string | null>(null);
    const setSuccessAlert = useSuccessAlert();
    const { data, loading: usersLoading } = useGetUsersWithoutRoleQuery({ variables: { contains: '' } });
    const [reassign, { loading }] = useReassignRequestMutation();
    const users = data?.UserList?.nodes ?? [];

    const handleConfirm = () => {
        if (!assignedToId) { setError('Please select a user.'); return; }
        if (!request?.id) { setError('The request could not be identified.'); return; }
        setError(null);
        reassign({ variables: { id: request.id, assignedToId, message: comment } })
            .then(() => { setSuccessAlert('Request Reassigned!'); onClose(); })
            .catch((err) => setError(err?.message || 'Unable to reassign the request.'));
    };

    return (
        <DialogBase title="Reassign Request" type="OkCancel" visible={visible} onClose={onClose}
            onOkClick={handleConfirm} isLoading={loading || usersLoading} style={style}>
            <div className="flex flex-col gap-3">
                <label className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Assign to</label>
                <select className="input-base" value={assignedToId} onChange={(e) => setAssignedToId(e.target.value)}>
                    <option value="">Select a user</option>
                    {users.map((candidate) => candidate?.id && (
                        <option key={candidate.id} value={candidate.id}>{candidate.name || candidate.email}</option>
                    ))}
                </select>
                <label className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Comment</label>
                <textarea className="input-base" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
                {error && <span className="text-xs" style={{ color: 'var(--error, #dc2626)' }}>{error}</span>}
            </div>
        </DialogBase>
    );
};

export default AssignResourceToRequestDialog;
